// The header's notification area: a bell, and status chips beside it.
//
// ⚠ TWO ZONES, ONE AREA, AND THEY MUST NOT SHARE A NUMBER. The badge renders
// `unread.total` and nothing else. Status items get their own chips with their
// own counts and are never added in. See shared/src/headerSummary.ts for why:
// if a status is inside the count, the count does not drop when the bell is
// read, and a number that will not clear is a number people stop looking at.
//
// ⚠ WHY STATUS IS IN THE HEADER rather than on home. The sticky panel already
// covers unanswered invitations and the day-of confirmation, but it exists on
// ONE screen. A running fast is true for days, and the complaint behind items
// 6 and 7 of the hand-test notes was the same both times: the member's own
// state is invisible the moment they navigate away. The header is the only
// surface on every page.

import { api } from '../api/client.js';
import { getAccessToken } from '../api/tokens.js';
import { el } from '../dom.js';
import { afterRender } from '../router.js';
import type { HeaderSummary, StatusItem, UnreadGroup } from '@msc/shared';

/**
 * Fire this on `document` to make the header re-read its summary immediately.
 *
 * For an in-page action that changes something the header counts but does NOT
 * navigate -- approving a join request is the case that prompted it. Without
 * this the chip keeps its old number until the next navigation or poll, so the
 * page and the header disagree about a queue the member just emptied.
 *
 * An EVENT rather than an exported function on purpose: any view can fire it
 * without importing the header, and firing it when no bell exists (a signed-out
 * page) is a harmless no-op rather than a crash.
 */
export const HEADER_REFRESH_EVENT = 'msc:header-refresh';

/** How often to re-poll while the tab is visible. */
const POLL_MS = 60_000;

let timer: number | null = null;
let openPanel: HTMLElement | null = null;

/**
 * Build the bell. Returns the element to place in the header.
 *
 * It fetches once on mount, again after every navigation, and on a slow poll.
 *
 * ⚠ NO REALTIME, deliberately. A `feed.post` frame could bump the count
 * live, but `realtime.ts` maps frames to channels by an explicit list and the
 * bell would need a subscription to every group the member belongs to -- which
 * is a lot of machinery for a number that is not time-critical. Per CLAUDE.md
 * the test is not "does this change" but "does a change have to arrive in
 * seconds", and a badge does not. If it is added later, note that a frame not
 * named in `dispatch()` is silently DROPPED.
 */
export function headerBell(): HTMLElement {
  const badge = el('span', { class: 'bell-badge', hidden: true });
  const button = el(
    'button',
    {
      type: 'button',
      class: 'bell-button',
      'aria-label': 'Notifications',
      'aria-expanded': 'false',
      'aria-haspopup': 'true',
    },
    [el('span', { class: 'bell-icon', 'aria-hidden': 'true' }, '🔔'), badge],
  );

  const chips = el('span', { class: 'header-chips' });
  const wrap = el('div', { class: 'header-bell' }, [button, chips]);

  let latest: HeaderSummary | null = null;

  const paint = (s: HeaderSummary): void => {
    latest = s;
    const n = s.unread.total;
    badge.textContent = n > 99 ? '99+' : String(n);
    // ⚠ `hidden` alone is not enough for anything with an author `display`
    // rule -- the attribute is only a UA rule and loses. `.bell-badge` and
    // `.chip` both carry a companion `[hidden] { display: none }` in the CSS
    // for exactly this reason. Setting the property is still correct; the CSS
    // is what makes it take effect.
    badge.hidden = n === 0;
    button.setAttribute(
      'aria-label',
      n === 0 ? 'Notifications' : `Notifications, ${n} unread`,
    );
    chips.replaceChildren(...s.status.map(statusChip));
  };

  const load = (): void => {
    // Only while the bell is actually in the header AND someone is signed in.
    // It is built once and its timer and afterRender hook outlive both: on a
    // bare page (/terms, /privacy, /login) the header is emptied, and after a
    // sign-out there is no token, so polling there was a 401 on every
    // navigation and every minute, filling the console.
    if (!wrap.isConnected || !getAccessToken()) return;
    void api<HeaderSummary>('/me/header-summary')
      .then(paint)
      // Silent. The bell is decoration on every page; a failed poll must never
      // surface an error over whatever the member is actually doing. The next
      // tick retries.
      .catch(() => {});
  };

  const startPolling = (): void => {
    if (timer !== null) window.clearInterval(timer);
    timer = window.setInterval(() => {
      // Skip while hidden: a background tab polling forever is wasted work,
      // and it comes straight back on the visibilitychange below.
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
  };

  button.addEventListener('click', () => {
    if (openPanel) {
      closePanel();
      return;
    }
    if (latest) showPanel(wrap, button, latest);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') load();
  });
  document.addEventListener(HEADER_REFRESH_EVENT, () => load());

  load();
  startPolling();
  // Re-read after navigation: opening a group's feed marks it read, and the
  // badge must reflect that without waiting up to a minute.
  afterRender(() => load());

  return wrap;
}

/** Copy for one status chip. Plain text; the client owns every word of this. */
function chipLabel(i: StatusItem): string {
  switch (i.kind) {
    case 'fast':
      return 'Fasting';
    case 'unanswered':
      return `${i.count} need${i.count === 1 ? 's' : ''} an answer`;
    case 'dayof':
      return 'Confirm tonight';
    case 'photo_queue':
      return `${i.count} photo${i.count === 1 ? '' : 's'} to approve`;
    case 'join_requests':
      return `${i.count} join request${i.count === 1 ? '' : 's'}`;
    default: {
      // Exhaustive: a new StatusKind is a compile error here rather than a
      // chip that renders as nothing.
      const never: never = i.kind;
      return never;
    }
  }
}

function chipHref(i: StatusItem): string {
  switch (i.kind) {
    case 'fast':
      return i.groupId === null ? '/' : `/groups/${i.groupId}/fasting`;
    case 'dayof':
      return i.eventId === null ? '/' : `/events/${i.eventId}`;
    case 'photo_queue':
      return i.groupId === null ? '/' : `/groups/${i.groupId}/photos`;
    case 'join_requests':
      // ⚠ #join-requests, not the bare page. Manage opens on Group photo and
      // the queue is ~two screens down; a count that does not land on the thing
      // it counted is a notification that makes you go looking.
      return i.groupId === null ? '/' : `/groups/${i.groupId}/manage#join-requests`;
    case 'unanswered':
    default:
      return '/';
  }
}

function statusChip(i: StatusItem): HTMLElement {
  const a = el(
    'a',
    { class: `chip chip-${i.kind}`, href: chipHref(i), title: i.groupName },
    chipLabel(i),
  );
  return a;
}

function closePanel(): void {
  if (!openPanel) return;
  openPanel.remove();
  openPanel = null;
  document.removeEventListener('click', onDocClick, true);
  document
    .querySelector('.bell-button')
    ?.setAttribute('aria-expanded', 'false');
}

function onDocClick(ev: MouseEvent): void {
  const t = ev.target as HTMLElement | null;
  if (!t) return;
  if (t.closest('.bell-panel') || t.closest('.bell-button')) return;
  closePanel();
}

function showPanel(host: HTMLElement, button: HTMLElement, s: HeaderSummary): void {
  const rows: HTMLElement[] =
    s.groups.length === 0
      ? [el('p', { class: 'bell-empty' }, 'Nothing new.')]
      : s.groups.map((g) => groupRow(g));

  const panel = el('div', { class: 'bell-panel', role: 'dialog', 'aria-label': 'Notifications' }, [
    el('div', { class: 'bell-panel-head' }, 'New since you last looked'),
    ...rows,
  ]);

  host.appendChild(panel);
  openPanel = panel;
  button.setAttribute('aria-expanded', 'true');
  // Capture phase, so a click on a link inside the panel still navigates while
  // a click anywhere else closes before that element's own handler runs.
  document.addEventListener('click', onDocClick, true);
}

function groupRow(g: UnreadGroup): HTMLElement {
  const bits: string[] = [];
  if (g.feed > 0) bits.push(`${g.feed} post${g.feed === 1 ? '' : 's'}`);
  if (g.chat > 0) bits.push(`${g.chat} message${g.chat === 1 ? '' : 's'}`);
  // Only conversation messages unread, in exactly one event: go straight to it.
  // Posts too (or several threads) means the group's feed, which summarizes each
  // event's conversation on its card.
  const href =
    g.chat > 0 && g.feed === 0 && g.chatEventId !== null
      ? `/events/${g.chatEventId}/chat`
      : `/groups/${g.groupId}`;
  const row = el('a', { class: 'bell-row', href }, [
    el('span', { class: 'bell-row-name' }, g.groupName),
    el('span', { class: 'bell-row-count' }, bits.join(', ')),
  ]);
  row.addEventListener('click', () => closePanel());
  return row;
}
